import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { EnvironmentStorage } from "../../../tools/EnvironmentStorage";
import { Loading } from "../../../components/Loading";
import { Drawer } from "../../../components/Drawer";
import { Button } from "../../../components/Button";
import { EnvironmentsAPI } from "../../../../providers/EnvironmentsAPI";
import { CredentialStep } from "./CredentialStep";
import { StoreStep } from "./StoreStep";
import { OrdaticAPI } from "../../../../providers/OrdaticAPI";
import styles from "@styles/integrations.module.scss"
import { Context } from "pages/Integrations";

dayjs.extend(utc);

const Steps = {
  CREDENTIAL: "CREDENTIAL",
  STORE: "STORE",
};

export default function OrdaticDrawer({ visible, onClose, onSave }) {
  const [step, setStep] = useState(Steps.CREDENTIAL);
  const [configurations, setConfigurations] = useState({
    appId: "",
    appSecret: "",
    storeId: "",
    environment: "PRODUCTION",
  });
  const [stores, setStores] = useState([]);

  const [loadingPage, setLoadingPage] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadConfigurations = async () => {
    setLoadingPage(true);

    const environmentId = EnvironmentStorage.getEnvironment().id;
    const externalCredentials = await EnvironmentsAPI.getConfigurationsByAlias(
      environmentId,
      "external_credentials"
    );
    const ordaticConfigs = externalCredentials.ordatic || {};

    if (Object.keys(ordaticConfigs).length > 0) {
      setConfigurations({
        appId: ordaticConfigs.appId,
        appSecret: ordaticConfigs.appSecret,
        storeId: ordaticConfigs.storeId,
        environment: ordaticConfigs.environment,
      });
    }

    setLoadingPage(false);
  };

  const updateConfigurations = async () => {
    const environmentId = EnvironmentStorage.getEnvironment().id;

    const externalCredentials = await EnvironmentsAPI.getConfigurationsByAlias(
      environmentId,
      "external_credentials"
    );
    
    const updateResponse = await EnvironmentsAPI.putConfigurationsByAlias(
      environmentId,
      "external_credentials",
      {
        ...externalCredentials,
        ordatic: {
          ...configurations,
          createdAt: dayjs().utc().format()
        },
      }
    );

    if (updateResponse.getErrors([]).length > 0) {
      alert({
        text: `Não foi possível alterar a configuração: ${
          updateResponse.getErrors()[0]
        }`,
      });
      return false;
    }

    return true;
  };

  const isValid = () => {
    if (step === Steps.CREDENTIAL) {
      return (
        configurations?.appId &&
        configurations?.appSecret &&
        configurations?.environment
      );
    }

    if (step === Steps.STORE) {
      return configurations?.storeId;
    }
  };

  const onConfirm = async () => {
    setLoading(true);

    if (step === Steps.CREDENTIAL) {
      await onUpdateCredentials();
    }

    if (step === Steps.STORE) {
      await onUpdateStore();
    }

    setLoading(false);
  };

  const onUpdateCredentials = async () => {
    await updateConfigurations();

    const environmentId = EnvironmentStorage.getEnvironment().id;

    const ordaticResponse = await OrdaticAPI.getStores(environmentId);
    if (ordaticResponse.getErrors([]).length > 0) {
      alert({
        text: ordaticResponse.getErrors()[0],
      });
      setLoading(false);
      return;
    }

    const stores = ordaticResponse.getData([]);

    setStores(stores);
    setStep(Steps.STORE);
    setLoading(false);
  }

  const onUpdateStore = async () => {
    const updated = await updateConfigurations();

    const environmentId = EnvironmentStorage.getEnvironment().id;

    const ordaticResponse = await OrdaticAPI.getStores(environmentId);
    if (ordaticResponse.getErrors([]).length > 0) {
      alert({
        text: ordaticResponse.getErrors()[0],
      });
      return;
    }

    await OrdaticAPI.syncStore(environmentId);
    return updated && onClose && onClose();
  }

  useEffect(() => {
    loadConfigurations();
  }, []);

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      closeInFade
      title={"Ordatic"}
      footer={
        <Button
          design="primary"
          className="w-100"
          onClick={() => onConfirm()}
          loading={loading}
          disabled={!isValid() || loading}
        >
          Salvar alterações
        </Button>
      }
    >
      <Context />
      
      {loadingPage ? (
        <div className="d-flex justify-center align-center h-100">
          <Loading fullScreen />
        </div>
      ) : (
        <div className={styles.scrollContainer}>
          {step === Steps.CREDENTIAL && (
            <CredentialStep
              appId={configurations?.appId}
              appSecret={configurations?.appSecret}
              environment={configurations?.environment}
              onChange={(updatedFields) =>
                setConfigurations({
                  ...configurations,
                  ...updatedFields,
                })
              }
            />
          )}

          {step === Steps.STORE && (
            <StoreStep
              stores={stores}
              storeId={configurations?.storeId}
              onChange={(updatedFields) =>
                setConfigurations({
                  ...configurations,
                  ...updatedFields,
                })
              }
            />
          )}
        </div>
      )}
    </Drawer>
  );
}
